import { TimestampDisplay } from '@/libs/components/src/lib/TimestampDisplay';
import { Button, ButtonColors } from '@chiroup/components/Button';
import { ConfirmModal } from '@chiroup/components/ConfirmModal';
import { CopyToClipboard } from '@chiroup/components/CopyToClipboard';
import { AUTOFILL_OFF } from '@chiroup/components/form/autofillOff';
import { Checkbox } from '@chiroup/components/form/Checkbox';
import { Input } from '@chiroup/components/form/Input';
import { Select } from '@chiroup/components/form/Select';
import { trivialDownload } from '@chiroup/components/TrivialDownload';
import { ColumnDef, DataTable } from '@chiroup/components/ui/data-table';
import { ChiroUpJSON } from '@chiroup/core/functions/ChiroUpJSON';
import {
  ConnectorOperationField,
  ConnectorOperationSummary,
} from '@chiroup/core/types/ConnectorAdmin.type';
import { ConnectorTraceRow } from '@chiroup/core/types/ConnectorTrace.type';
import { useEffect, useMemo, useState } from 'react';
import { useConnectorOperations } from '../../hooks/useConnectionApi';
import { connectorErrorMessage } from './connectorErrorMessage';

/**
 * Operations (INFRA-11): manually execute any vendor capability with typed
 * inputs — through the SAME core function the product path calls. The form is
 * rendered from the descriptor's fields (server-derived from its Joi schema);
 * this page ships NOTHING per operation. New descriptors appear here on their
 * own.
 */

type FieldValues = Record<string, string | boolean>;

const defaultsFor = (operation: ConnectorOperationSummary | null): FieldValues => {
  const values: FieldValues = {};

  for (const field of operation?.fields ?? []) {
    if (field.default !== undefined) {
      values[field.name] =
        field.type === 'boolean' ? field.default === true : String(field.default);
    }
  }

  return values;
};

/** One schema-driven input: enum → Select, boolean → Checkbox, else Input. */
const OperationInput = ({
  field,
  value,
  onChange,
}: {
  field: ConnectorOperationField;
  value: string | boolean | undefined;
  onChange: (val: string | boolean) => void;
}) => {
  const label = `${field.name}${field.required ? ' *' : ''}`;

  if (field.type === 'boolean') {
    return (
      <Checkbox
        name={`op-${field.name}`}
        label={label}
        value={value === true}
        onChange={(val: boolean) => onChange(val)}
      />
    );
  }

  if (field.allow?.length) {
    return (
      <Select
        name={`op-${field.name}`}
        label={label}
        value={value === undefined ? undefined : String(value)}
        options={field.allow.map(a => ({ value: String(a), text: String(a) }))}
        onChange={(val: string) => onChange(val)}
        hint={field.description}
      />
    );
  }

  return (
    <Input
      name={`op-${field.name}`}
      label={label}
      value={value === undefined ? '' : String(value)}
      onChange={(val: string) => onChange(val)}
      hint={field.description}
      {...AUTOFILL_OFF}
    />
  );
};

type OperationRunData = {
  operation?: string;
  by?: string;
  outcome?: string;
  durationMs?: number;
  keyName?: string;
};

/**
 * A result whose top-level `curl` is a string reproduces its vendor call from
 * a terminal — rendered as its own copyable block, generically for any
 * operation that provides one.
 */
const curlOf = (result: unknown): string | null => {
  const curl = (result as { curl?: unknown } | null)?.curl;

  return typeof curl === 'string' && curl ? curl : null;
};

export const Operations = () => {
  const [selectedVendor, setSelectedVendor] = useState<string | null>(null);
  const [selectedOperation, setSelectedOperation] = useState<string | null>(null);
  const [values, setValues] = useState<FieldValues>({});
  const [prefillValues, setPrefillValues] = useState<FieldValues>({});
  const [prefillNote, setPrefillNote] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [runsRequested, setRunsRequested] = useState(false);
  const [selectedRun, setSelectedRun] = useState<ConnectorTraceRow | null>(null);

  // Reads sized to the click: the bare list on mount, ONE operation's schema
  // on selection, run history only when asked for. Nothing per-op or per-run
  // rides the console read. The hook derives the effective vendor/operation.
  const {
    vendorNames,
    vendor,
    catalog,
    operationName,
    operation,
    isCatalogLoading,
    runs,
    runOperation,
    prefill,
  } = useConnectorOperations({ selectedVendor, selectedOperation, runsRequested });

  // Schema defaults land when the selected operation's schema arrives.
  useEffect(() => {
    setValues(defaultsFor(operation));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operation?.name]);

  const pickOperation = (name: string) => {
    setSelectedOperation(name);
    setValues({});
    setPrefillValues({});
    setPrefillNote(null);
    runOperation.reset();
    prefill.reset();
  };

  const prefillFieldNames = new Set((operation?.prefill?.fields ?? []).map(f => f.name));
  const mainFields = (operation?.fields ?? []).filter(f => !prefillFieldNames.has(f.name));
  const prefillReady = (operation?.prefill?.fields ?? []).every(
    f => !f.required || String(prefillValues[f.name] ?? '').trim() !== ''
  );

  const fetchPrefill = () => {
    const input: Record<string, unknown> = {};

    for (const field of operation?.prefill?.fields ?? []) {
      const value = prefillValues[field.name];

      if (value === undefined || value === '') continue;

      input[field.name] = field.type === 'number' ? Number(value) : value;
    }

    prefill.mutate(
      { vendor: vendor ?? '', operation: operation?.name ?? '', input },
      {
        onSuccess: result => {
          setPrefillNote(result.note ?? null);
          // Fetched values land in the EDITABLE fields — review, then Run.
          setValues(prev => ({
            ...prev,
            ...Object.fromEntries(
              Object.entries(result.values).map(([k, v]) => [
                k,
                typeof v === 'boolean' ? v : String(v),
              ])
            ),
          }));
        },
      }
    );
  };

  const missingRequired = mainFields.some(
    f => f.required && (values[f.name] === undefined || String(values[f.name]).trim() === '')
  );

  // Only touched/defaulted fields are sent — the schema owns real validation.
  const inputPayload = useMemo(() => {
    const payload: Record<string, unknown> = {};

    for (const field of mainFields) {
      const value = values[field.name];

      if (value === undefined || value === '') continue;

      payload[field.name] = field.type === 'number' ? Number(value) : value;
    }

    return payload;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operation, values]);

  const execute = (confirm?: boolean) =>
    runOperation.mutate({
      vendor: vendor ?? '',
      operation: operation?.name ?? '',
      input: inputPayload,
      ...(confirm ? { confirm } : {}),
    });

  const runColumns: ColumnDef<ConnectorTraceRow>[] = [
    {
      accessorKey: 'createdAt',
      header: 'When',
      cell: ({ row }) => <TimestampDisplay ts={row.original.createdAt} format="MM/DD HH:mm:ss" />,
    },
    { accessorKey: 'vendor', header: 'Vendor' },
    {
      id: 'operation',
      header: 'Operation',
      cell: ({ row }) => (
        <span className="font-mono text-xs">
          {(row.original.data as OperationRunData | null)?.operation ?? '—'}
        </span>
      ),
    },
    {
      id: 'outcome',
      header: 'Outcome',
      cell: ({ row }) => {
        const data = (row.original.data ?? {}) as OperationRunData;

        return (
          <span
            className={`tabular-nums ${
              data.outcome === 'success' ? 'text-green-700' : 'text-red-700'
            }`}
          >
            {data.outcome ?? '—'}
            {typeof data.durationMs === 'number' ? ` (${data.durationMs}ms)` : ''}
          </span>
        );
      },
    },
    {
      id: 'key',
      header: 'Key',
      cell: ({ row }) => (
        <span className="font-mono text-xs">
          {(row.original.data as OperationRunData | null)?.keyName ?? '—'}
        </span>
      ),
    },
    {
      id: 'by',
      header: 'By',
      cell: ({ row }) => (row.original.data as OperationRunData | null)?.by ?? '—',
    },
  ];

  return (
    <div className="space-y-8 p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Operations</h1>
        <p className="mt-1 max-w-[56ch] text-sm text-gray-500">
          Every run executes the same core function the product path calls, and leaves a durable
          activity row.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        {vendorNames.length > 1 ? (
          <div className="w-52">
            <Select
              name="operations-vendor"
              label="Vendor"
              value={vendor ?? undefined}
              options={vendorNames.map(v => ({ value: v, text: v }))}
              onChange={(val: string) => {
                setSelectedVendor(val);
                setSelectedOperation(null);
                setValues({});
                runOperation.reset();
              }}
            />
          </div>
        ) : null}
        <div className="w-80">
          <Select
            name="operations-operation"
            label="Operation"
            value={operationName ?? undefined}
            options={catalog.map(op => ({
              value: op.name,
              text: `${op.title}${op.mutating ? ' (mutating)' : ''}`,
            }))}
            onChange={pickOperation}
          />
        </div>
      </div>

      {operation ? (
        <div className="rounded-lg border border-gray-950/10 p-5">
          <div className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h2 className="text-sm font-semibold text-gray-900">{operation.title}</h2>
            <div className="font-mono text-xs text-gray-400">
              {operation.name} → '{operation.exchangeType}'
            </div>
            {operation.mutating ? (
              <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-amber-600/20 ring-inset">
                changes vendor state
              </span>
            ) : null}
          </div>

          {operation.prefill ? (
            <div className="mb-5 rounded-md bg-gray-50 p-4 ring-1 ring-gray-950/5">
              <div className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                Prefill from a transaction
              </div>
              <div className="mt-2 flex flex-wrap items-end gap-3">
                {operation.prefill.fields.map(field => (
                  <div key={field.name} className="w-72">
                    <OperationInput
                      field={field}
                      value={prefillValues[field.name]}
                      onChange={val => setPrefillValues(prev => ({ ...prev, [field.name]: val }))}
                    />
                  </div>
                ))}
                <Button
                  color={ButtonColors.primary}
                  loading={prefill.isLoading}
                  disabled={!prefillReady}
                  onClick={fetchPrefill}
                  text="Fetch"
                />
              </div>
              {prefill.error ? (
                <p className="mt-3 text-sm text-red-600">
                  {connectorErrorMessage(prefill.error, 'The fetch failed.')}
                </p>
              ) : null}
              {prefillNote ? <p className="mt-3 text-xs text-gray-600">{prefillNote}</p> : null}
            </div>
          ) : null}

          <div className="grid max-w-4xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
            {mainFields.map(field => (
              <OperationInput
                key={field.name}
                field={field}
                value={values[field.name]}
                onChange={val => setValues(prev => ({ ...prev, [field.name]: val }))}
              />
            ))}
          </div>

          <div className="mt-5 flex items-center gap-3">
            <Button
              color={ButtonColors.primary}
              loading={runOperation.isLoading}
              disabled={missingRequired}
              onClick={() => (operation.mutating ? setConfirming(true) : execute())}
              text="Run"
            />
            {runOperation.error ? (
              <span className="text-sm text-red-600">
                {connectorErrorMessage(runOperation.error, 'The operation failed.')}
              </span>
            ) : null}
          </div>

          {runOperation.data ? (
            <div className="mt-5 border-t border-gray-950/5 pt-4">
              <div className="mb-2 flex items-baseline gap-x-3">
                <h3 className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                  Result
                </h3>
                <div className="text-xs text-gray-500 tabular-nums">
                  {runOperation.data.outcome} in {runOperation.data.durationMs}ms
                </div>
                <CopyToClipboard
                  text={ChiroUpJSON.pretty(runOperation.data.result)}
                  title="Copy result"
                />
              </div>
              <pre className="max-h-96 overflow-auto rounded-md bg-gray-50 p-4 text-xs/5 ring-1 ring-gray-950/5">
                {ChiroUpJSON.pretty(runOperation.data.result)}
              </pre>
              {curlOf(runOperation.data.result) ? (
                <div className="mt-4">
                  <div className="mb-2 flex items-baseline gap-x-3">
                    <h3 className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                      Same call from a terminal
                    </h3>
                    <span className="text-xs text-gray-500">
                      Set <code>STEDI_API_KEY</code> to the key&apos;s material first.
                    </span>
                    <CopyToClipboard
                      text={curlOf(runOperation.data.result) ?? ''}
                      title="Copy curl"
                    />
                  </div>
                  <pre className="overflow-auto rounded-md bg-gray-50 p-4 text-xs/5 break-all whitespace-pre-wrap ring-1 ring-gray-950/5">
                    {curlOf(runOperation.data.result)}
                  </pre>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-gray-500">
          {isCatalogLoading ? 'Loading the operation catalog…' : 'No operations are available.'}
        </p>
      )}

      <div>
        <div className="mb-3 flex items-center gap-3">
          <h2 className="text-sm font-semibold text-gray-900">Recent runs</h2>
          <Button
            color={ButtonColors.plain}
            loading={runsRequested && runs.isFetching}
            onClick={() => (runsRequested ? runs.refetch() : setRunsRequested(true))}
            text={runs.data ? 'Refresh' : 'Load'}
          />
        </div>
        {runs.data ? (
          <DataTable
            columns={runColumns}
            data={runs.data.operationRuns}
            onRowClick={row => setSelectedRun(prev => (prev?.id === row.id ? null : row))}
          />
        ) : (
          <p className="text-sm text-gray-500">Run history is fetched on demand.</p>
        )}
        {selectedRun ? (
          // The row already carries its payload (full input/result for
          // mock-class runs; identifiers only for phi) — no extra fetch.
          <div className="mt-3">
            <div className="mb-2 flex items-center gap-3">
              <span className="font-mono text-xs text-gray-500">
                {(selectedRun.data as OperationRunData | null)?.operation ?? 'run'} #
                {selectedRun.id}
              </span>
              <Button
                color={ButtonColors.plain}
                onClick={() =>
                  navigator.clipboard.writeText(ChiroUpJSON.pretty(selectedRun.data ?? {}))
                }
                text="Copy"
              />
              <Button
                color={ButtonColors.plain}
                onClick={() =>
                  trivialDownload({
                    content: ChiroUpJSON.pretty(selectedRun.data ?? {}),
                    name: `operation-run-${selectedRun.id}.json`,
                    mimeType: 'application/json',
                  })
                }
                text="Download"
              />
            </div>
            <pre className="max-h-96 overflow-auto rounded-md bg-gray-50 p-4 text-xs/5 ring-1 ring-gray-950/5">
              {ChiroUpJSON.pretty(selectedRun.data ?? {})}
            </pre>
          </div>
        ) : null}
      </div>

      {confirming && operation ? (
        <ConfirmModal
          isOpen={true}
          confirm={() => {
            setConfirming(false);
            execute(true);
          }}
          close={() => setConfirming(false)}
          confirmText="Run it"
          description={`'${operation.title}' changes vendor state. Run it against ${vendor}?`}
        />
      ) : null}
    </div>
  );
};

export default Operations;
